/**
 * The shop page's own cover photo, falling back to the booking/profile
 * cover a salon may have set instead. ONE definition — `ShopHero` (the big
 * banner) and the page-level cover tint (`Pages/Shop/index.js`,
 * `hooks/useCoverTint.js`) must agree on which image "the cover" even is,
 * or the tint could end up derived from a different photo than the one the
 * shopper actually sees.
 */
export function resolveShopCoverUrl(salon) {
  return salon?.shopCoverImageUrl ?? salon?.coverImageUrl ?? salon?.coverPhoto ?? null;
}

/**
 * …AND ONE DEFINITION OF WHETHER THAT COVER IS A VIDEO.
 *
 * This pattern lived in `ShopHero.js` alone, so the renderer knew a video
 * cover was a video and the tint hook did not: `useCoverTint` loaded every
 * cover into an `Image`, an `.mp4` failed that load, and a shop with a video
 * cover got no derived tint at all and fell through to the theme-colour wash.
 * The two agree by reading the same regular expression now.
 */
const VIDEO_COVER = /\.(mp4|webm|mov|ogg|m4v)(\?.*)?$/i;

export function isVideoCover(url) {
  return VIDEO_COVER.test(url || '');
}

/**
 * WHICH FRAME OF A CLIP IS THE SHOP'S COLOUR.
 *
 * Not frame zero, and that is the whole reason this is a function rather than
 * a `drawImage` on whatever is loaded: covers open on a fade, and a fade's
 * first frame samples as flat black or flat white. Either one would tint the
 * entire shop a colour that appears nowhere in the video.
 *
 * A quarter of the way in is past any opening fade on a short loop, which is
 * what a shop cover is. It is FLOORED at half a second so a clip whose
 * duration we could not read is still not frame zero, CAPPED at five seconds
 * so a long clip does not leave the page on the fallback while it seeks, and
 * clamped inside the clip's own length so a very short loop is not asked to
 * seek past its end.
 */
export const FRAME_FRACTION = 0.25;
export const FRAME_MIN_SECONDS = 0.5;
export const FRAME_MAX_SECONDS = 5;

export function representativeFrameTime(duration) {
  if (!Number.isFinite(duration) || duration <= 0) return FRAME_MIN_SECONDS;
  const wanted = Math.min(Math.max(duration * FRAME_FRACTION, FRAME_MIN_SECONDS), FRAME_MAX_SECONDS);
  // Never past the end: seeking beyond the duration fires no `seeked` at all.
  return Math.min(wanted, Math.max(duration - 0.05, duration * FRAME_FRACTION));
}

export default resolveShopCoverUrl;
